import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Search, Trash2, Edit, Crown, Star, Sparkles, User, Mail, Calendar, ChevronDown, RefreshCw, Filter, X, Dumbbell, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminClients = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, free: 0, principiante: 0, intermedio: 0, premium: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newLevel, setNewLevel] = useState('');
  const [updating, setUpdating] = useState(false);
  const [editTab, setEditTab] = useState('level');
  const [subStartDate, setSubStartDate] = useState('');
  const [subEndDate, setSubEndDate] = useState('');
  const [extendDays, setExtendDays] = useState('');
  const [extendMonths, setExtendMonths] = useState('');
  const [renewLevel, setRenewLevel] = useState('premium');
  const [renewMonths, setRenewMonths] = useState('12');
  const [regeneratingUserId, setRegeneratingUserId] = useState(null);
  const [allPrograms, setAllPrograms] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);

  const handleRegenerateRoutine = async (user) => {
    const ok = window.confirm(`¿Regenerar la rutina de ${user.name}? Esto reemplazará su rutina actual con una nueva.`);
    if (!ok) return;
    setRegeneratingUserId(user.user_id);
    try {
      const token = localStorage.getItem('session_token') || localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${user.user_id}/regenerate-routine`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Rutina regenerada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo regenerar la rutina');
    } finally {
      setRegeneratingUserId(null);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAllPrograms();
  }, []);

  const fetchAllPrograms = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/programs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllPrograms(res.data || []);
    } catch (err) {
      console.error('Error fetching programs:', err);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users?exclude_test=true&exclude_admins=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
      setStats(response.data.stats || { total: 0, free: 0, principiante: 0, intermedio: 0, premium: 0 });
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setNewLevel(user.subscription_level || 'free');
    setEditTab('level');
    setSubStartDate(user.subscription_start_at ? user.subscription_start_at.split('T')[0] : '');
    setSubEndDate(user.subscription_expires_at ? user.subscription_expires_at.split('T')[0] : '');
    setExtendDays('');
    setExtendMonths('');
    setRenewLevel(user.subscription_level || 'premium');
    setRenewMonths('12');
    setSelectedPrograms(user.purchased_programs || []);
    setShowEditModal(true);
  };

  const toggleProgramForUser = (programId) => {
    setSelectedPrograms(prev =>
      prev.includes(programId) ? prev.filter(p => p !== programId) : [...prev, programId]
    );
  };

  const handleUpdatePrograms = async () => {
    if (!selectedUser) return;
    setUpdating(true);
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${selectedUser.user_id}/update`,
        { purchased_programs: selectedPrograms },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Programas adquiridos actualizados');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar programas');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateLevel = async () => {
    if (!selectedUser) return;
    setUpdating(true);
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${selectedUser.user_id}/level`, 
        { subscription_level: newLevel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Nivel actualizado a ${getLevelName(newLevel)}`);
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar nivel');
    } finally {
      setUpdating(false);
    }
  };

  const handleSubscriptionAction = async (action, payload) => {
    if (!selectedUser) return;
    setUpdating(true);
    const token = localStorage.getItem('session_token');
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/users/${selectedUser.user_id}/subscription`,
        { action, ...payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Suscripcion actualizada');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar suscripcion');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Estás seguro de eliminar a ${user.name || user.email}?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${user.user_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const getLevelName = (level) => {
    const names = {
      'free': 'Gratis',
      'principiante': 'Nivel 1',
      'intermedio': 'Nivel 2',
      'premium': 'Premium'
    };
    return names[level] || level || 'Gratis';
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'premium':
        return <Crown className="w-4 h-4 text-amber-400" />;
      case 'intermedio':
        return <Star className="w-4 h-4 text-purple-400" />;
      case 'principiante':
        return <Sparkles className="w-4 h-4 text-pink-400" />;
      default:
        return <User className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getLevelBadgeClass = (level) => {
    switch (level) {
      case 'premium':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'intermedio':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'principiante':
        return 'bg-pink-500/20 text-pink-400 border-pink-500/30';
      default:
        return 'bg-zinc-700/50 text-zinc-400 border-zinc-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('es-CO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  // Filtrar usuarios
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      (user.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const matchesLevel = levelFilter === 'all' || 
      (levelFilter === 'free' && (!user.subscription_level || user.subscription_level === 'free')) ||
      user.subscription_level === levelFilter;
    
    return matchesSearch && matchesLevel;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" className="text-zinc-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Users className="w-6 h-6 text-pink-500" />
                Gestión de Clientes
              </h1>
              <p className="text-zinc-400 text-sm mt-1">
                Administra todos los usuarios registrados en la plataforma
              </p>
            </div>
          </div>
          <Button 
            onClick={fetchUsers}
            className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-pink-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-zinc-500">Total</p>
              </div>
            </div>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-zinc-700/50 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-zinc-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.free}</p>
                <p className="text-xs text-zinc-500">Gratis</p>
              </div>
            </div>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-500/20 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-pink-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.principiante}</p>
                <p className="text-xs text-zinc-500">Nivel 1</p>
              </div>
            </div>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.intermedio}</p>
                <p className="text-xs text-zinc-500">Nivel 2</p>
              </div>
            </div>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Crown className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.premium}</p>
                <p className="text-xs text-zinc-500">Premium</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-white placeholder-zinc-500 focus:border-pink-500 focus:outline-none"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="pl-10 pr-10 py-3 bg-zinc-900 border border-zinc-800 rounded-xl text-white appearance-none cursor-pointer focus:border-pink-500 focus:outline-none min-w-[150px]"
            >
              <option value="all">Todos los niveles</option>
              <option value="free">Gratis</option>
              <option value="principiante">Nivel 1</option>
              <option value="intermedio">Nivel 2</option>
              <option value="premium">Premium</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-800">
            <h2 className="text-white font-semibold">
              Usuarios ({filteredUsers.length})
            </h2>
          </div>

          {filteredUsers.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 text-lg">
                {searchTerm || levelFilter !== 'all' 
                  ? 'No se encontraron usuarios con estos filtros' 
                  : 'No hay usuarios registrados'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-800/50">
                  <tr>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Usuario</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Email</th>
                    <th className="text-center px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Nivel</th>
                    <th className="text-center px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Registro</th>
                    <th className="text-center px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Compra</th>
                    <th className="text-center px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Vencimiento</th>
                    <th className="text-center px-6 py-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {filteredUsers.map((user) => (
                    <tr key={user.user_id} className="hover:bg-zinc-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                            {(user.name || user.email || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.name || '-'}</p>
                            {user.phone && (
                              <p className="text-zinc-500 text-xs">{user.phone}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Mail className="w-4 h-4" />
                          <span className="text-sm">{user.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${getLevelBadgeClass(user.subscription_level)}`}>
                          {getLevelIcon(user.subscription_level)}
                          {getLevelName(user.subscription_level)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1 text-zinc-500 text-sm">
                          <Calendar className="w-4 h-4" />
                          {formatDate(user.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {user.subscription_purchased_at ? (
                          <span className="text-xs text-blue-300 font-medium">
                            {formatDate(user.subscription_purchased_at)}
                          </span>
                        ) : (
                          <span className="text-zinc-600 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {user.subscription_expires_at ? (
                          <span className={`text-xs font-medium ${
                            new Date(user.subscription_expires_at) > new Date() ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {formatDate(user.subscription_expires_at)}
                          </span>
                        ) : (
                          <span className="text-zinc-600 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRegenerateRoutine(user)}
                            disabled={regeneratingUserId === user.user_id}
                            className="text-pink-400 hover:text-pink-300 hover:bg-pink-500/10"
                            title="Regenerar rutina de entrenamiento"
                            data-testid={`regenerate-routine-${user.user_id}`}
                          >
                            {regeneratingUserId === user.user_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Dumbbell className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(user)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            title="Cambiar nivel"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            title="Eliminar usuario"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-3">📋 Información</h3>
          <ul className="text-zinc-400 text-sm space-y-2">
            <li>• <strong>Gratis:</strong> Usuarios registrados sin plan de pago</li>
            <li>• <strong>Nivel 1:</strong> Acceso a contenido principiante ($39)</li>
            <li>• <strong>Nivel 2:</strong> Acceso a contenido intermedio ($59)</li>
            <li>• <strong>Premium:</strong> Acceso completo a todo el contenido ($199)</li>
            <li className="text-pink-400">• Los usuarios con pagos reales NO pueden ser eliminados desde aquí</li>
          </ul>
        </div>
      </div>

      {/* Edit Modal with Subscription Management */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-zinc-800 flex justify-between items-center sticky top-0 bg-zinc-900 z-10">
              <div>
                <h2 className="text-lg font-bold text-white">Gestionar Usuario</h2>
                <p className="text-zinc-400 text-sm">{selectedUser.name || selectedUser.email}</p>
              </div>
              <button onClick={() => { setShowEditModal(false); setSelectedUser(null); }} className="text-zinc-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-zinc-800">
              {[
                { id: 'level', label: 'Nivel' },
                { id: 'programs', label: 'Programas' },
                { id: 'dates', label: 'Fechas' },
                { id: 'extend', label: 'Extender' },
                { id: 'renew', label: 'Renovar' },
              ].map(tab => (
                <button key={tab.id} onClick={() => setEditTab(tab.id)}
                  className={`flex-1 py-3 text-xs font-bold transition-all ${
                    editTab === tab.id ? 'text-pink-400 border-b-2 border-pink-500' : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                  data-testid={`edit-user-tab-${tab.id}`}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-5">
              {/* TAB: Nivel */}
              {editTab === 'level' && (
                <div className="space-y-3">
                  <p className="text-zinc-400 text-sm mb-4">Cambiar nivel de suscripcion:</p>
                  {[
                    { value: 'free', label: 'Gratis', desc: 'Sin acceso de pago', icon: User, color: 'zinc' },
                    { value: 'principiante', label: 'Nivel 1', desc: 'Contenido basico', icon: Sparkles, color: 'pink' },
                    { value: 'intermedio', label: 'Nivel 2', desc: 'Contenido intermedio', icon: Star, color: 'purple' },
                    { value: 'premium', label: 'Premium', desc: 'Acceso completo', icon: Crown, color: 'amber' }
                  ].map((level) => {
                    const Icon = level.icon;
                    const isSelected = newLevel === level.value;
                    return (
                      <button key={level.value} onClick={() => setNewLevel(level.value)}
                        className={`w-full p-3 rounded-xl border-2 transition-all text-left flex items-center gap-3 ${
                          isSelected ? 'border-pink-500 bg-pink-500/10' : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                        }`}>
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-pink-400' : 'text-zinc-400'}`} />
                        <div className="flex-1">
                          <p className={`font-medium text-sm ${isSelected ? 'text-white' : 'text-zinc-300'}`}>{level.label}</p>
                          <p className="text-[10px] text-zinc-500">{level.desc}</p>
                        </div>
                        {isSelected && <span className="text-pink-400 text-lg">&#10003;</span>}
                      </button>
                    );
                  })}
                  <Button onClick={handleUpdateLevel} disabled={updating || newLevel === selectedUser.subscription_level}
                    className="w-full mt-4 bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50">
                    {updating ? 'Guardando...' : 'Guardar Nivel'}
                  </Button>
                </div>
              )}

              {/* TAB: Programas Adquiridos */}
              {editTab === 'programs' && (
                <div className="space-y-3">
                  <p className="text-zinc-400 text-sm mb-1">Programas adquiridos por la usuaria:</p>
                  <p className="text-zinc-500 text-xs mb-4">
                    Se guardarán en <code className="text-pink-400">purchased_programs</code>. Este listado manda sobre el contenido nuevo (program_ids). Si queda vacío, el sistema respeta el nivel de suscripción tradicional.
                  </p>
                  {allPrograms.length === 0 ? (
                    <p className="text-xs text-zinc-500">No hay programas creados en el sistema.</p>
                  ) : (
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {allPrograms.map(prog => {
                        const isSel = selectedPrograms.includes(prog.program_id);
                        return (
                          <label key={prog.program_id}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              isSel ? 'border-pink-500 bg-pink-500/10' : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                            }`}
                            data-testid={`user-program-${prog.program_id}`}>
                            <input
                              type="checkbox"
                              checked={isSel}
                              onChange={() => toggleProgramForUser(prog.program_id)}
                              className="w-4 h-4 accent-pink-600"
                            />
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm truncate ${isSel ? 'text-white' : 'text-zinc-300'}`}>
                                {prog.name || prog.title || prog.program_id}
                              </p>
                              <p className="text-[10px] text-zinc-500 truncate">
                                {prog.level || '—'} · {prog.program_id}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <Button onClick={handleUpdatePrograms} disabled={updating}
                    className="w-full mt-3 bg-pink-600 hover:bg-pink-500 text-white disabled:opacity-50"
                    data-testid="save-user-programs-btn">
                    {updating ? 'Guardando...' : 'Guardar Programas'}
                  </Button>
                </div>
              )}

              {/* TAB: Fechas */}
              {editTab === 'dates' && (
                <div className="space-y-4">
                  <p className="text-zinc-400 text-sm">Modificar fechas de suscripcion manualmente:</p>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Fecha de inicio</label>
                    <input type="date" value={subStartDate} onChange={(e) => setSubStartDate(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Fecha de vencimiento</label>
                    <input type="date" value={subEndDate} onChange={(e) => setSubEndDate(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm" />
                  </div>
                  {selectedUser.subscription_expires_at && (
                    <p className="text-xs text-zinc-500">
                      Vencimiento actual: <span className={new Date(selectedUser.subscription_expires_at) > new Date() ? 'text-green-400' : 'text-red-400'}>
                        {new Date(selectedUser.subscription_expires_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    </p>
                  )}
                  <Button onClick={() => handleSubscriptionAction('set_dates', { subscription_start: subStartDate, subscription_end: subEndDate })}
                    disabled={updating || (!subStartDate && !subEndDate)}
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50">
                    {updating ? 'Guardando...' : 'Guardar Fechas'}
                  </Button>
                </div>
              )}

              {/* TAB: Extender */}
              {editTab === 'extend' && (
                <div className="space-y-4">
                  <p className="text-zinc-400 text-sm">Agregar tiempo a la suscripcion actual:</p>
                  {selectedUser.subscription_expires_at && (
                    <p className="text-xs text-zinc-500">
                      Vence: <span className="text-white">{new Date(selectedUser.subscription_expires_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Dias</label>
                      <input type="number" value={extendDays} onChange={(e) => setExtendDays(e.target.value)}
                        placeholder="0" min="0" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Meses</label>
                      <input type="number" value={extendMonths} onChange={(e) => setExtendMonths(e.target.value)}
                        placeholder="0" min="0" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm" />
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { label: '+7 dias', d: 7, m: 0 },
                      { label: '+15 dias', d: 15, m: 0 },
                      { label: '+1 mes', d: 0, m: 1 },
                      { label: '+3 meses', d: 0, m: 3 },
                      { label: '+6 meses', d: 0, m: 6 },
                      { label: '+1 año', d: 0, m: 12 },
                    ].map(opt => (
                      <button key={opt.label} onClick={() => { setExtendDays(String(opt.d)); setExtendMonths(String(opt.m)); }}
                        className="px-3 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs hover:border-pink-500 hover:text-pink-400">
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <Button onClick={() => handleSubscriptionAction('extend', { days: parseInt(extendDays) || 0, months: parseInt(extendMonths) || 0 })}
                    disabled={updating || (!extendDays && !extendMonths) || (parseInt(extendDays || 0) === 0 && parseInt(extendMonths || 0) === 0)}
                    className="w-full bg-green-600 hover:bg-green-500 text-white disabled:opacity-50">
                    {updating ? 'Extendiendo...' : 'Extender Suscripcion'}
                  </Button>
                </div>
              )}

              {/* TAB: Renovar */}
              {editTab === 'renew' && (
                <div className="space-y-4">
                  <p className="text-zinc-400 text-sm">Crear nueva suscripcion (reinicia fechas):</p>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Plan</label>
                    <select value={renewLevel} onChange={(e) => setRenewLevel(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm">
                      <option value="principiante">Nivel 1 - Principiante</option>
                      <option value="intermedio">Nivel 2 - Intermedio</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Duracion</label>
                    <select value={renewMonths} onChange={(e) => setRenewMonths(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white text-sm">
                      <option value="1">1 mes</option>
                      <option value="3">3 meses</option>
                      <option value="6">6 meses</option>
                      <option value="12">1 año</option>
                      <option value="24">2 años</option>
                    </select>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
                    <p className="text-amber-300 text-xs">
                      Esto cambiara el plan a <strong>{renewLevel === 'premium' ? 'Premium' : renewLevel === 'intermedio' ? 'Nivel 2' : 'Nivel 1'}</strong> con fecha de inicio <strong>hoy</strong> y vencimiento en <strong>{renewMonths} {parseInt(renewMonths) === 1 ? 'mes' : 'meses'}</strong>.
                    </p>
                  </div>
                  <Button onClick={() => handleSubscriptionAction('renew', { subscription_level: renewLevel, duration_months: parseInt(renewMonths) })}
                    disabled={updating}
                    className="w-full bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50">
                    {updating ? 'Renovando...' : 'Renovar Suscripcion'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClients;
